import {MouseEvent, useCallback} from 'react'
import {useSelector} from 'react-redux'
import {useHandleOfferClick} from 'components/Offer/Shared/OfferInteraction/useHandleOfferClick'
import {useOfferTrackingContextWithFallback} from 'context/offerTracking'
import {getOfferContext} from 'middleware/analytics/selectors'
import {
  getHotelOfferEntity,
  getOfferAccessFlowSettings
} from 'modules/sapiSearch/selectors'
import {getClickType, isTrackableAlternativeClick} from 'utils/clickType'

import {getClosestDataIdToEventTarget} from '@vio/core/src/_web/utils/browser'
import {ClickType} from '@vio/core/src/analytics/types/Events'
import {useOpticks} from '@vio/core/src/experiments/components/OpticksProvider'
import getIsBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {noop} from '@vio/core/src/utils/function'
import {useLogIn} from '@vio/shared/src/authentication/hooks/useLogIn'

import {useShouldOfferLeadToRooms} from './useShouldOfferLeadToRooms'

interface Props {
  component: string
  hotelId: string
  offer: SearchOffer
  /** @deprecated Uses outdated components from before the rebranding. Only used in the deprecated `components/OfferList` component. */
  offerPosition: number
  isSelectedOffer?: boolean
  onClick?: (offer: SearchOffer) => void
  isDiscount?: boolean
  isSplitBooking?: boolean
  logIn?: ReturnType<typeof useLogIn>
}

export const useOfferClickHandler = ({
  component,
  hotelId,
  offer,
  offerPosition,
  onClick,
  isDiscount,
  isSplitBooking,
  logIn = noop
}: Props) => {
  // Allow context to overwrite default selectors
  const {offerContext} = useOfferTrackingContextWithFallback({
    offer,
    offerEntity: useSelector(state => getHotelOfferEntity(state, hotelId)),
    offerContext: useSelector(state =>
      getOfferContext(state, hotelId, offer?.id, component)
    )
  })
  const isBofhOffer = getIsBofhOffer(offer)
  const onOfferClicked = useHandleOfferClick({
    hotelId,
    sourceComponent: component,
    offer,
    isSplitBooking,
    logIn
  })
  const Opticks = useOpticks()
  const shouldGoToRooms = useShouldOfferLeadToRooms(isBofhOffer)

  const {isAppLocked} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, offer?.id)
  )
  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()

      const selectedElementId = getClosestDataIdToEventTarget(event)

      if (Opticks) {
        Opticks.setAudienceSegmentationAttributes({
          isVioClicker: isBofhOffer
        })
      }

      if (shouldGoToRooms && !isAppLocked) {
        event.preventDefault()
      }

      onOfferClicked({
        offer,
        selectedElementId,
        offerContext,
        isDiscount,
        clickType: ClickType.LeftClick
      })

      onClick?.(offer)
    },
    [onClick, offerContext, offerPosition, onOfferClicked, offer, isBofhOffer]
  )

  /**
   * Right, middle and long-press clicks open the offer without ever firing a
   * plain `click`, so they need their own tracking. The browser owns the
   * navigation here, so this deliberately does not preventDefault.
   */
  const handleAlternativeClick = useCallback(
    (event: MouseEvent) => {
      if (!isTrackableAlternativeClick(event)) return

      onOfferClicked({
        offer,
        selectedElementId: getClosestDataIdToEventTarget(event),
        offerContext,
        isDiscount,
        clickType: getClickType(event)
      })
    },
    [offerContext, onOfferClicked, offer, isDiscount]
  )

  return {
    handleClick,
    handleAlternativeClick
  }
}
